import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Check, ArrowUpRight, ArrowUp } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { buttonVariants } from '@/components/ui/Button'
import {
  type Product,
  type ProductCategory,
  type ProductStatus,
} from '@/constants/products'
import { cn } from '@/lib/cn'

interface ProductsDirectoryGridProps {
  products: readonly Product[]
  selectedProductId: string
  onSelectProduct: (productId: string) => void
}

const categories: readonly ProductCategory[] = [
  'All',
  'Mobile Platforms',
  'Enterprise & ERP',
  'Retail & Commerce',
]

const statusConfig: Record<ProductStatus, { label: string; dotClass: string }> = {
  'Production Foundation': {
    label: 'Production Ready',
    dotClass: 'bg-success',
  },
  'In Development': {
    label: 'Active Engineering',
    dotClass: 'bg-info',
  },
  'Planning Phase': {
    label: 'Planning Phase',
    dotClass: 'bg-fg-muted',
  },
  'Coming Soon': {
    label: 'Coming Soon',
    dotClass: 'bg-warning',
  },
}

export function ProductsDirectoryGrid({
  products,
  selectedProductId,
  onSelectProduct,
}: ProductsDirectoryGridProps) {
  const [selectedCategory, setSelectedCategory] = useState<ProductCategory>('All')

  const filteredProducts =
    selectedCategory === 'All'
      ? products
      : products.filter((p) => p.category === selectedCategory)

  const handleInspect = (productId: string) => {
    onSelectProduct(productId)
    const element = document.getElementById('spotlight')
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <section className="py-12 sm:py-16 md:py-20">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 sm:mb-12">
        <div className="space-y-2">
          <span className="font-mono text-xs uppercase tracking-widest text-fg-muted block">
            // Complete Systems Catalog
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-fg">
            All Production Systems
          </h2>
          <p className="text-sm text-fg-subtle max-w-xl">
            A comprehensive index of HUN TECH proprietary architectures, cross-platform client solutions, and commercial engines.
          </p>
        </div>

        {/* Filter Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-2 md:pb-0 shrink-0">
          {categories.map((category) => {
            const isSelected = selectedCategory === category
            return (
              <button
                type="button"
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-sm transition-colors cursor-pointer select-none shrink-0 border',
                  isSelected
                    ? 'bg-btn-primary text-btn-primary-fg border-transparent shadow-xs'
                    : 'bg-surface-2 text-fg-subtle border-border-subtle hover:text-fg hover:bg-surface-3'
                )}
              >
                {category}
              </button>
            )
          })}
        </div>
      </div>

      {/* Directory Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProducts.map((product) => {
          const isCurrentActive = product.id === selectedProductId
          const status = statusConfig[product.status]

          return (
            <Card
              key={product.id}
              surface="2"
              className={cn(
                'border p-5 sm:p-6 flex flex-col justify-between transition-all duration-200',
                isCurrentActive
                  ? 'border-fg/40 shadow-sm'
                  : 'border-border hover:border-border-strong'
              )}
            >
              <div className="space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-xl font-bold tracking-tight text-fg">
                        {product.name}
                      </h3>
                      {product.amharicName && (
                        <span className="font-mono text-xs px-1.5 py-0.5 rounded-xs bg-surface-3 text-fg-muted border border-border-subtle">
                          {product.amharicName}
                        </span>
                      )}
                    </div>
                    <span className="font-mono text-[11px] text-fg-muted uppercase">
                      {product.category}
                    </span>
                  </div>

                  <span className="flex items-center gap-1.5 text-[11px] text-fg-subtle font-mono font-medium shrink-0 pt-1">
                    <span className={cn('h-1.5 w-1.5 rounded-full', status.dotClass)} />
                    {status.label}
                  </span>
                </div>

                {/* Headline & Description */}
                <p className="text-xs sm:text-sm text-fg-subtle leading-relaxed line-clamp-3">
                  {product.description}
                </p>

                {/* Key Capabilities */}
                <div className="pt-2 border-t border-border-subtle">
                  <span className="text-[10px] font-mono font-semibold uppercase tracking-wider text-fg-muted block mb-2">
                    Key Architectural Capabilities
                  </span>
                  <ul className="space-y-1.5 list-none p-0 m-0">
                    {product.keyCapabilities.slice(0, 3).map((cap) => (
                      <li
                        key={cap}
                        className="flex items-start gap-1.5 text-xs text-fg-subtle leading-snug"
                      >
                        <Check className="w-3.5 h-3.5 text-fg shrink-0 mt-0.5" />
                        <span className="line-clamp-1">{cap}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Tech Stack */}
                <div className="pt-2 border-t border-border-subtle">
                  <div className="flex flex-wrap gap-1">
                    {product.techStack.map((tech) => (
                      <span
                        key={tech}
                        className="font-mono text-[10px] px-1.5 py-0.5 rounded-xs bg-surface-1 text-fg border border-border-subtle"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Actions Footer */}
              <div className="pt-5 mt-5 border-t border-border-subtle flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => handleInspect(product.id)}
                  className={buttonVariants({
                    variant: isCurrentActive ? 'secondary' : 'primary',
                    size: 'sm',
                    className: 'flex-1 text-xs gap-1.5 cursor-pointer',
                  })}
                >
                  <ArrowUp className="w-3.5 h-3.5" />
                  <span>{isCurrentActive ? 'Viewing Architecture ↑' : 'Inspect Architecture ↑'}</span>
                </button>

                <Link
                  to="/contact"
                  className={buttonVariants({
                    variant: 'outline',
                    size: 'sm',
                    className: 'text-xs px-3',
                  })}
                  title={`Inquire about ${product.name}`}
                >
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </Card>
          )
        })}
      </div>
    </section>
  )
}
